import {
  animate,
  MotionValue,
  Reorder,
  useDragControls,
  useMotionValue,
} from 'framer-motion';
import React, { FC, useEffect } from 'react';
import ReorderIcon from './ReorderIcon';
import AttributeHeader, { AttributeHeaderProps } from './AttributeHeader';

interface CardReorder extends AttributeHeaderProps {
  initialItems: string[];
  items: string[];
  setItems: (items: string[]) => void;
}

const CardReorder: FC<CardReorder> = ({
  initialItems,
  items,
  setItems,
  ...attributeHeaderProps
}) => {
  return (
    <>
      <AttributeHeader {...attributeHeaderProps} />
      <div className="flex gap-2 px-6">
        <div className="flex flex-col gap-2">
          <div className="flex h-12 items-center justify-center rounded border border-stroke-base-subdued px-2 text-xs font-bold text-content-base-subdued">
            Primary
          </div>
          <div className="flex h-12 items-center justify-center rounded border border-stroke-base-subdued px-2 text-xs font-bold text-content-base-subdued">
            Secondary
          </div>
          <div className="flex h-12 items-center justify-center rounded border border-stroke-base-subdued px-2 text-xs font-bold text-content-base-subdued">
            Tertiary
          </div>
        </div>
        <Reorder.Group
          axis="y"
          onReorder={setItems}
          values={items}
          className="flex flex-1 flex-col gap-2"
        >
          {items.map((item) => (
            <Item key={item} item={item} />
          ))}
        </Reorder.Group>
      </div>
    </>
  );
};

export default CardReorder;

export const Item = ({ item }: { item: string }) => {
  const y = useMotionValue(0);
  const boxShadow = useRaisedShadow(y);
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={item}
      id={item}
      style={{ boxShadow, y }}
      dragListener={false}
      dragControls={dragControls}
      className="flex h-12 items-center justify-start gap-2 rounded-md bg-background-base-surface-0 px-2 text-sm font-semibold text-content-base-default"
    >
      <span
        onPointerDown={(event) => dragControls.start(event)}
        className="cursor-grab text-content-base-subdued"
      >
        <ReorderIcon />
      </span>
      <span>{item}</span>
    </Reorder.Item>
  );
};

const inactiveShadow = '0px 0px 0px rgba(0,0,0,0.8)';

export function useRaisedShadow(value: MotionValue<number>) {
  const boxShadow = useMotionValue(inactiveShadow);

  useEffect(() => {
    let isActive = false;
    value.onChange((latest) => {
      const wasActive = isActive;
      if (latest !== 0) {
        isActive = true;
        if (isActive !== wasActive) {
          animate(boxShadow, '5px 5px 10px rgba(0,0,0,0.3)');
        }
      } else {
        isActive = false;
        if (isActive !== wasActive) {
          animate(boxShadow, inactiveShadow);
        }
      }
    });
  }, [value, boxShadow]);

  return boxShadow;
}
